import { useCallback } from 'react';
import { Combobox, Loader, TextInput, useCombobox } from '@mantine/core';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { captureException } from '@sentry/react';
import clsx from 'clsx';
import { Icon, IconButton } from '@/components';
import { ArrowLeft, Search } from 'react-feather';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { clearQuery } from '@/features/filterSlice';
import {
  useNavigateWithSearch,
  withPreservedSearch,
} from '@/hooks/useNavigateWithSearch';
import type { SuggestionsByType } from '@/features/searchSlice';
import { useClientTerminology } from '@/hooks/useClientTerminology';
import {
  coordinateSuggester,
  useAppSuggester,
  useMapboxSuggester,
} from './suggesters';
import { useSearchEngine } from './useSearchEngine';
import { NoSuggestions } from './NoSuggestions';
import { OptionLabel } from './OptionLabel';
import classes from './SearchBar.module.css';

interface Props {
  isFullScreen?: boolean;
  mapCenter: { lat: number; lng: number };
  bbox?: number[];
}

/**
 * Public entry point. Reads `filterQuery.changeCount` and uses it as the inner
 * component's `key` so any external filter change remounts the inner shell —
 * fully resetting input, results, cache, and dropdown state without an
 * effect-driven setState cascade.
 */
export const SearchBar = (props: Props) => {
  const cacheKey = useAppSelector((state) => state.filterQuery.changeCount);
  return <SearchBarInner key={cacheKey} {...props} />;
};

const SearchBarInner = ({ isFullScreen = false, mapCenter, bbox }: Props) => {
  const navigate = useNavigate();
  const navigateWithSearch = useNavigateWithSearch();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const intl = useClientTerminology();

  const isFilterActive = useAppSelector(
    (state) => state.filterQuery.isSelected
  );

  const appSuggester = useAppSuggester();
  const mapboxSuggester = useMapboxSuggester({ mapCenter, bbox });

  const search = useCallback(
    async (term: string): Promise<SuggestionsByType> => {
      const [app, addresses] = await Promise.all([
        appSuggester(term),
        mapboxSuggester(term),
      ]);
      // Coordinates first (most specific), then app results, then addresses.
      return {
        Coordinates: coordinateSuggester(term),
        Spans: app.Spans ?? [],
        Poles: app.Poles ?? [],
        Addresses: addresses,
        SpansInternal: app.SpansInternal ?? [],
      };
    },
    [appSuggester, mapboxSuggester]
  );

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const { inputValue, onChange, results, isFetching, getSuggestion } =
    useSearchEngine({ search, combobox });

  const handleSubmit = (value: string) => {
    const match = getSuggestion(value);
    if (!match) return;
    combobox.closeDropdown();
    if (match.kind === 'link') {
      navigateWithSearch(match.href, { sessionOnly: true });
    } else {
      match.onSelect().catch(captureException);
    }
  };

  /**
   * Clears the active filter set. `clearQuery` bumps `filterQuery.changeCount`,
   * which the public `SearchBar` reads as a `key` — remounting this component
   * and resetting all local search state.
   */
  const handleClearFilter = () => {
    dispatch(clearQuery());
  };

  const showEmptyState =
    inputValue.length > 0 &&
    !isFetching &&
    results.every((g) => g.options.length === 0);

  /**
   * Search input with a floating loader overlay. The loader sits absolutely
   * positioned rather than in Mantine's `rightSection` slot — that slot
   * reserves padding on the input which truncates the placeholder. Floating
   * overlay = full input width AND no reflow when `isFetching` toggles.
   */
  const input = (
    <div className={classes.inputContainer}>
      <TextInput
        data-testid="search-bar-input"
        placeholder={intl.formatMessage({ id: 'search.placeholder' })}
        value={inputValue}
        onChange={onChange}
        autoFocus={isFullScreen}
        classNames={{
          input: isFullScreen ? classes.fullScreenInput : classes.input,
        }}
        leftSection={
          <span className={classes.searchIcon}>
            <Icon Component={Search} size={16} color="currentColor" />
          </span>
        }
        leftSectionWidth={40}
      />
      {isFetching && (
        <span className={classes.loaderOverlay}>
          <Loader size="xs" type="dots" />
        </span>
      )}
    </div>
  );

  const target = isFullScreen ? (
    <div className={classes.fullScreenTopBar}>
      <span data-testid="search-bar-back" className={classes.backButton}>
        <IconButton
          Component={ArrowLeft}
          iconSize={24}
          color="currentColor"
          onClick={() => navigate(-1)}
        />
      </span>
      <Combobox.Target>
        <div className={classes.fullScreenInputWrapper}>{input}</div>
      </Combobox.Target>
    </div>
  ) : (
    <Combobox.Target>{input}</Combobox.Target>
  );

  const dropdown = (
    <Combobox.Dropdown
      className={clsx(
        classes.dropdown,
        isFullScreen && classes.fullScreenDropdown
      )}
    >
      <Combobox.Options>
        {showEmptyState && (
          <Combobox.Empty>
            <NoSuggestions
              hasFilters={isFilterActive}
              onClearFilter={handleClearFilter}
            />
          </Combobox.Empty>
        )}
        {!showEmptyState &&
          results.map((group) =>
            group.options.length === 0 ? null : (
              <Combobox.Group
                key={group.label}
                label={
                  <span className={classes.groupLabel}>
                    {intl.formatMessage({
                      id: `search.groupLabel.${group.label}`,
                    })}
                  </span>
                }
              >
                {group.options.map((suggestion) => {
                  const label = (
                    <OptionLabel
                      group={group.label}
                      suggestion={suggestion}
                      searchTerm={inputValue}
                    />
                  );
                  const optionValue = `${group.label}:${suggestion.value}`;
                  return (
                    <Combobox.Option
                      key={optionValue}
                      value={optionValue}
                      data-testid={`search-bar-option-${suggestion.value}`}
                    >
                      {suggestion.kind === 'link' ? (
                        <Link
                          to={withPreservedSearch(
                            suggestion.href,
                            location.search,
                            { sessionOnly: true }
                          )}
                          className={classes.optionLink}
                          // Plain left-click: suppress React Router so handleSubmit
                          // owns navigation. Modifier-click (Cmd/Ctrl/Shift) and
                          // middle-click pass through so the browser can open in
                          // a new tab/window natively.
                          onClick={(e) => {
                            if (
                              e.metaKey ||
                              e.ctrlKey ||
                              e.shiftKey ||
                              e.button !== 0
                            ) {
                              return;
                            }
                            e.preventDefault();
                          }}
                        >
                          {label}
                        </Link>
                      ) : (
                        label
                      )}
                    </Combobox.Option>
                  );
                })}
              </Combobox.Group>
            )
          )}
      </Combobox.Options>
    </Combobox.Dropdown>
  );

  return (
    <div
      className={isFullScreen ? classes.fullScreenRoot : classes.searchBarRoot}
      data-testid="search-bar"
    >
      <Combobox
        store={combobox}
        onOptionSubmit={handleSubmit}
        withinPortal={false}
      >
        {target}
        {dropdown}
      </Combobox>
    </div>
  );
};
